import type { FormState } from './ApplicationForm.types';

/** Textfelder aus dem FormData, damit sie nach einem Fehler wieder befuellt werden (wie im Server). */
export const extractInputs = (formData: FormData): Record<string, string | undefined> =>
  Object.fromEntries(
    Array.from(formData.entries())
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
      .map(([key, value]) => [key, value || undefined]),
  );

const isFormState = (value: unknown): value is FormState =>
  typeof value === 'object' && value !== null && typeof (value as FormState).success === 'boolean';

type PostApplicationParams = {
  customerCompanyNumber: string;
  postOfferId: string;
  locale: string;
  formData: FormData;
};

/**
 * Schickt die Bewerbung an /api/applications. Wirft, wenn keine Antwort im FormState-Format kommt
 * (Netzwerkfehler, 502 vom Load Balancer, HTML-Fehlerseite); das Formular faengt das ab.
 */
export const postApplication = async ({
  customerCompanyNumber,
  postOfferId,
  locale,
  formData,
}: PostApplicationParams): Promise<FormState> => {
  const query = new URLSearchParams({ customerCompanyNumber, postOfferId, locale });
  const response = await fetch(`/api/applications?${query}`, { method: 'POST', body: formData });

  const body: unknown = await response.json().catch(() => undefined);
  if (!isFormState(body)) {
    throw new Error(`Application request failed with status ${response.status}`);
  }

  return { ...body, inputs: body.inputs ?? (body.success ? undefined : extractInputs(formData)) };
};
